import { useState, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/core/auth/auth";
import { toast } from "sonner";

// Custom section type
export interface CustomSection {
  id: string;
  user_id: string;
  section_token: string;
  section_title: string;
  default_text: string | null;
  exam_family_id: string | null;
  scope: "template" | "user" | "org";
  created_at: string;
}

// Custom structure type
export interface CustomStructure {
  id: string;
  user_id: string;
  structure_key: string;
  display_name: string;
  body_region: string | null;
  section_token: string | null;
  scope: "template" | "user" | "org";
  created_at: string;
}

// Create section input
export interface CreateCustomSectionInput {
  section_token: string;
  section_title: string;
  default_text?: string;
  exam_family_id?: string;
  scope: "template" | "user" | "org";
}

// Create structure input
export interface CreateCustomStructureInput {
  structure_key: string;
  display_name: string;
  body_region?: string;
  section_token?: string;
  scope: "template" | "user" | "org";
}

interface UseCustomSectionsResult {
  sections: CustomSection[];
  loading: boolean;
  fetch: (examFamilyId?: string) => Promise<void>;
  create: (input: CreateCustomSectionInput) => Promise<CustomSection | null>;
  remove: (id: string) => Promise<boolean>;
}

interface UseCustomStructuresResult {
  structures: CustomStructure[];
  loading: boolean;
  fetch: (examFamilyId?: string) => Promise<void>;
  create: (input: CreateCustomStructureInput) => Promise<CustomStructure | null>;
  remove: (id: string) => Promise<boolean>;
}

/**
 * Hook for managing user's custom sections
 */
export function useCustomSections(): UseCustomSectionsResult {
  const { user } = useAuth();
  const [sections, setSections] = useState<CustomSection[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (examFamilyId?: string) => {
    if (!user) {
      setSections([]);
      return;
    }

    setLoading(true);
    try {
      let query = supabase
        .from("user_custom_sections")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      // If examFamilyId provided, get template-scoped + user-scoped sections
      if (examFamilyId) {
        query = query.or(`exam_family_id.eq.${examFamilyId},scope.eq.user`);
      } else {
        // Otherwise just get user-scoped sections
        query = query.eq("scope", "user");
      }

      const { data, error } = await query;

      if (error) throw error;

      setSections((data || []) as CustomSection[]);
    } catch (err) {
      console.error("Failed to fetch custom sections:", err);
      toast.error("Failed to load custom sections");
      setSections([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const create = useCallback(async (input: CreateCustomSectionInput): Promise<CustomSection | null> => {
    if (!user) {
      toast.error("You must be logged in to create custom sections");
      return null;
    }

    try {
      const { data, error } = await supabase
        .from("user_custom_sections")
        .insert({
          user_id: user.id,
          section_token: input.section_token,
          section_title: input.section_title,
          default_text: input.default_text || null,
          exam_family_id: input.exam_family_id || null,
          scope: input.scope,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          toast.error("A section with this token already exists");
        } else {
          throw error;
        }
        return null;
      }

      toast.success("Custom section created");
      setSections((prev) => [data as CustomSection, ...prev]);
      return data as CustomSection;
    } catch (err) {
      console.error("Failed to create custom section:", err);
      toast.error("Failed to create custom section");
      return null;
    }
  }, [user]);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    if (!user) {
      toast.error("You must be logged in to delete custom sections");
      return false;
    }

    try {
      const { error } = await supabase
        .from("user_custom_sections")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;

      toast.success("Custom section deleted");
      setSections((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch (err) {
      console.error("Failed to delete custom section:", err);
      toast.error("Failed to delete custom section");
      return false;
    }
  }, [user]);

  return {
    sections,
    loading,
    fetch,
    create,
    remove,
  };
}

/**
 * Hook for managing user's custom structures
 */
export function useCustomStructures(): UseCustomStructuresResult {
  const { user } = useAuth();
  const [structures, setStructures] = useState<CustomStructure[]>([]);
  const [loading, setLoading] = useState(false);

  const fetch = useCallback(async (examFamilyId?: string) => {
    if (!user) {
      setStructures([]);
      return;
    }

    setLoading(true);
    try {
      let query = supabase
        .from("user_custom_structures")
        .select("*")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false });

      // Get user-scoped structures (template-scoped would be filtered by exam)
      if (!examFamilyId) {
        query = query.eq("scope", "user");
      }

      const { data, error } = await query;

      if (error) throw error;

      setStructures((data || []) as CustomStructure[]);
    } catch (err) {
      console.error("Failed to fetch custom structures:", err);
      toast.error("Failed to load custom structures");
      setStructures([]);
    } finally {
      setLoading(false);
    }
  }, [user]);

  const create = useCallback(async (input: CreateCustomStructureInput): Promise<CustomStructure | null> => {
    if (!user) {
      toast.error("You must be logged in to create custom structures");
      return null;
    }

    try {
      const { data, error } = await supabase
        .from("user_custom_structures")
        .insert({
          user_id: user.id,
          structure_key: input.structure_key,
          display_name: input.display_name,
          body_region: input.body_region || null,
          section_token: input.section_token || null,
          scope: input.scope,
        })
        .select()
        .single();

      if (error) {
        if (error.code === "23505") {
          toast.error("A structure with this key already exists");
        } else {
          throw error;
        }
        return null;
      }

      toast.success("Custom structure created");
      setStructures((prev) => [data as CustomStructure, ...prev]);
      return data as CustomStructure;
    } catch (err) {
      console.error("Failed to create custom structure:", err);
      toast.error("Failed to create custom structure");
      return null;
    }
  }, [user]);

  const remove = useCallback(async (id: string): Promise<boolean> => {
    if (!user) {
      toast.error("You must be logged in to delete custom structures");
      return false;
    }

    try {
      const { error } = await supabase
        .from("user_custom_structures")
        .delete()
        .eq("id", id)
        .eq("user_id", user.id);

      if (error) throw error;

      toast.success("Custom structure deleted");
      setStructures((prev) => prev.filter((s) => s.id !== id));
      return true;
    } catch (err) {
      console.error("Failed to delete custom structure:", err);
      toast.error("Failed to delete custom structure");
      return false;
    }
  }, [user]);

  return {
    structures,
    loading,
    fetch,
    create,
    remove,
  };
}
